import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, Trash2, Copy, Check, Image as ImageIcon, X } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import AdminSearch from './AdminSearch';

const absoluteUrl = (u) => (u && u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
};

const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState('');
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/admin/gallery`, { headers: adminAuthHeader() });
      setItems(res.data || []);
    } catch (e) {
      setErr('Failed to load gallery');
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    setErr('');
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        await axios.post(`${API}/admin/upload`, form, {
          headers: { ...adminAuthHeader(), 'Content-Type': 'multipart/form-data' },
        });
      }
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const remove = async (filename) => {
    if (!window.confirm('Delete this image from the server? This will remove it from disk permanently.')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${encodeURIComponent(filename)}`, { headers: adminAuthHeader() });
      await load();
    } catch (_) { setErr('Could not delete image'); }
  };

  const copyUrl = async (item) => {
    const full = absoluteUrl(item.url);
    try {
      await navigator.clipboard.writeText(full);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = full; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (__) {}
      document.body.removeChild(ta);
    }
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(''), 1500);
  };

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Media Gallery</h2>
          <p className="text-sm text-gray-500">All images uploaded to the server. Reuse them anywhere by copying their URL.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
            data-testid="gallery-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="gallery-upload-btn"
            className="flex items-center gap-1.5 bg-[#0d64a8] hover:bg-[#0a548e] disabled:opacity-70 text-white text-sm px-4 py-2 rounded-md"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading…' : 'Upload Images'}
          </button>
        </div>
      </div>

      <div className="mb-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search filename or original name..." testId="gallery-search" />
      </div>


      {err && <div className="bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-2 rounded-md mb-4">{err}</div>}

      {loading ? (
        <div className="text-center py-16 text-gray-500 text-sm">Loading gallery…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-[#e8f2fb] text-[#0d64a8] mx-auto flex items-center justify-center mb-2">
            <ImageIcon size={20} />
          </div>
          <p className="text-gray-700 font-medium">No images uploaded yet</p>
          <button onClick={() => fileRef.current?.click()} className="mt-4 bg-[#0d64a8] text-white text-sm px-5 py-2 rounded-md">
            Upload your first image
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="gallery-grid">
          {items.filter((it) => {
            const s = q.trim().toLowerCase();
            if (!s) return true;
            return [it.filename, it.original_name, it.url].filter(Boolean).join(' ').toLowerCase().includes(s);
          }).map((it) => (
            <div
              key={it.id || it.filename}
              className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition"
              data-testid={`gallery-item-${it.filename}`}
            >
              <div
                className="relative aspect-square bg-gray-50 cursor-zoom-in overflow-hidden"
                onClick={() => setPreview(it)}
              >
                <img
                  src={absoluteUrl(it.url)}
                  alt={it.original_name || it.filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 flex items-end justify-between opacity-0 group-hover:opacity-100 transition">
                  <span className="text-[10px] text-white">{fmtBytes(it.size)}</span>
                </div>
              </div>
              <div className="p-2">
                <div className="text-[11px] text-gray-700 truncate" title={it.original_name || it.filename}>
                  {it.original_name || it.filename}
                </div>
                <div className="text-[10px] text-gray-400 mb-1.5">{fmtDate(it.uploaded_at)}</div>
                <div className="flex gap-1">
                  <button
                    onClick={() => copyUrl(it)}
                    data-testid={`gallery-copy-${it.filename}`}
                    className={`flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 rounded border transition ${
                      copiedId === it.id
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'border-gray-200 text-gray-700 hover:border-[#0d64a8] hover:text-[#0d64a8]'
                    }`}
                  >
                    {copiedId === it.id ? <><Check size={11}/> Copied</> : <><Copy size={11}/> Copy URL</>}
                  </button>
                  <button
                    onClick={() => remove(it.filename)}
                    data-testid={`gallery-delete-${it.filename}`}
                    className="w-8 flex items-center justify-center text-[11px] text-red-500 border border-gray-200 rounded hover:border-red-400 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="max-w-4xl w-full bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="text-sm font-medium text-gray-800 truncate">{preview.original_name || preview.filename}</div>
              <button onClick={() => setPreview(null)} className="text-gray-500 hover:text-gray-800"><X size={18} /></button>
            </div>
            <div className="bg-gray-50 max-h-[70vh] overflow-auto flex items-center justify-center">
              <img src={absoluteUrl(preview.url)} alt="" className="max-w-full max-h-[70vh] object-contain" />
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-600">
              <input readOnly value={absoluteUrl(preview.url)} className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs" onFocus={(e) => e.target.select()} />
              <button onClick={() => copyUrl(preview)} className="text-[11px] px-3 py-1.5 border border-gray-200 rounded hover:border-[#0d64a8] hover:text-[#0d64a8]">
                {copiedId === preview.id ? 'Copied!' : 'Copy URL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
